
import { z } from "zod";

export const parseEnv = <T>(
  schema: z.ZodSchema,
  envObject: Record<string, unknown>,
): T => {
  try {
    return schema.parse(envObject) as T;
  } catch (error) {
    if (error instanceof z.ZodError) {
      const missingVars = error.issues
        .map((err) => err.path.join("."))
        .join(", ");
      throw new Error(
        `Missing or invalid environment variables: ${missingVars}`,
      );
    }

    throw error;
  }
};



const envSchema = z.object({
  VITE_BASE_URL: z.string(),
  VITE_GOOGLE_MAPS_API_KEY: z.string(),
  // Gate signup behind an invite code in the UI. Server-side enforcement is
  // independent (SIGNUP_INVITE_REQUIRED on the backend); this only controls whether
  // the invite field + "express interest" fallback are shown. Anything other than the
  // literal "true" (unset, empty string, or an invalid value) means off — an unset CI
  // variable renders as an empty string at build time, which must not crash the app.
  VITE_REQUIRE_INVITE_CODE: z.enum(["true", "false"]).catch("false"),
  // Feature flag for the redesigned employer dashboard (SPEC: Employer Dashboard v2).
  // Anything other than the literal "true" (unset, empty, invalid) keeps the old
  // dashboard — an unset CI var is an empty string at build time and must not crash.
  VITE_EMPLOYER_DASHBOARD_V2: z.enum(["true", "false"]).catch("false"),
  // Feature flag for the redesigned worker dashboard (SPEC: Worker Dashboard v2).
  // Same semantics as the employer flag — only the literal "true" enables it; unset/
  // empty/invalid (an unset CI var is an empty string at build time) keeps the old page.
  VITE_WORKER_DASHBOARD_V2: z.enum(["true", "false"]).catch("false"),
  // Feature flag for employer subscription billing (billing-subscription-plan/DESIGN.md):
  // the owner-onboarding Payment Element step, the billing status gate / resolution
  // page and Settings → Billing. Only the literal "true" enables it; unset / empty /
  // invalid keeps today's behavior (step hidden, no status query, no gate).
  VITE_SUBSCRIPTION_BILLING: z.enum(["true", "false"]).catch("false"),
  DEV: z.boolean(),
});

export const env = parseEnv<ClientEnv>(envSchema, {
  VITE_BASE_URL: import.meta.env.VITE_BASE_URL,
  VITE_GOOGLE_MAPS_API_KEY: import.meta.env.VITE_GOOGLE_MAPS_API_KEY,
  VITE_REQUIRE_INVITE_CODE: import.meta.env.VITE_REQUIRE_INVITE_CODE,
  VITE_EMPLOYER_DASHBOARD_V2: import.meta.env.VITE_EMPLOYER_DASHBOARD_V2,
  VITE_WORKER_DASHBOARD_V2: import.meta.env.VITE_WORKER_DASHBOARD_V2,
  VITE_SUBSCRIPTION_BILLING: import.meta.env.VITE_SUBSCRIPTION_BILLING,
  DEV: import.meta.env.DEV,
});

export type ClientEnv = z.infer<typeof envSchema>;

export default env;

